import { useEffect, useState, useRef } from "react";
import queryString from "query-string";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import * as Sentry from "@sentry/browser";
import { prepareResponse, submitPreSurvey, updateRespondentDevice } from "../../api/Project";
import Loader from "../../themes/components/Loader";
import { notify, notifySimple } from "../../themes/components/Notifications";
import PreSurvey from "../../components/Suppliers/PreSurvey";
import { postRequest } from "../../utils/Axios";

const KEY = "obsrvnow-response";
const SENTRY_PAGE_TAG = "responses-prepare";

type RequestTokenResponse = {
  token: string;
  channel: string;
  countryCode: string;
};

const ResponsePrepareRoute: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true),
    [questions, setQuestions] = useState<ProjectSupplierQuestion[]>([]),
    [respondentId, setRespondentId] = useState<string>(""),
    invokeApiRef = useRef(null);

  const urlParams = queryString.parse(document.location.search);

  const projectId = typeof urlParams["obsrv_pid"] === "string" ? urlParams["obsrv_pid"] : "";
  const project_supplier_id =
    typeof urlParams["obsrv_sid"] === "string" ? urlParams["obsrv_sid"] : "";
  let roundtableAIScript: HTMLScriptElement | null = null;

  const capturePageException = (error: unknown, context: string) => {
    try {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: { page: SENTRY_PAGE_TAG },
          extra: { context },
        });
        return;
      }
    } catch (err) {
      console.log("Failed to capture exception in Sentry:", err);
    }
  };

  const evaluateDevice = async (
    respondentId: string,
    projectSupplierId: string,
    projectId: string
  ): Promise<JsonObject | null> => {
    if (!invokeApiRef.current) {
      console.warn("Forensiq script is not yet initialized");
      return null;
    }
    try {
      const result: JsonObject | null = await invokeApiRef.current(respondentId, projectSupplierId, projectId);
      return result;
    } catch (error) {
      capturePageException(error, "evaluateDevice_failed");
      return null;
    }
  };

  const fetchRoundtableSessionId = (): string | null => {
    const sessionId = sessionStorage.getItem("rtSessionId");

    if (sessionId) return sessionId;

    return typeof window.getRoundtableSessionId === "function"
      ? window.getRoundtableSessionId()
      : null;
  };

  const loadRoundtable = (respondentId: string): Promise<void> => {
    return new Promise((resolve) => {
      const roundtableAIKey: string = import.meta.env.VITE_APP_ROUNDTABLEAI_SITE_KEY || null;

      // The roundtable:ready event should be dispatched by the Roundtable script 
      // when it's fully initialized and has set the session ID in sessionStorage.
      //  We listen for that event to know when to check for the session ID.
      // There are instances when the event might fire but the id is not yet in sessionStorage 
      // due to async timing, so we also implement a polling mechanism as a fallback.
      let sessionCheckInterval: NodeJS.Timeout | null = null;
      let timeout = 10000; // Max wait time of 10 seconds

      const checkForSessionId = () => {
        timeout -= 100;
        const sessionId = fetchRoundtableSessionId();
        if (sessionId || timeout <= 0) {
          if (sessionCheckInterval) clearInterval(sessionCheckInterval);
          resolve();
          return true;
        }
        return false;
      };

      const handleRoundtableReady = () => {
        // Check immediately after small delay
        if (checkForSessionId()) return;

        // If not found, start polling sessionStorage
        sessionCheckInterval = setInterval(checkForSessionId, 100);
      };

      // Listen for the ready event
      window.addEventListener("roundtable:ready", handleRoundtableReady, { once: true });

      // ---- Roundtable Script Injection ----
      roundtableAIScript = document.createElement("script");
      roundtableAIScript.src = "https://cdn.roundtable.ai/v1/rt.js";
      roundtableAIScript.async = true;
      roundtableAIScript.setAttribute("data-site-key", roundtableAIKey);
      roundtableAIScript.setAttribute("data-user-id", respondentId);
      roundtableAIScript.id = "roundtableai-script";

      roundtableAIScript.onerror = () => {
        console.error("Roundtable script failed to load");
        if (sessionCheckInterval) clearInterval(sessionCheckInterval);
        resolve(); // fail-open so users can continue
      };

      document.body.appendChild(roundtableAIScript);
    });
  };

  const onPrepareResponse = async () => {
    try {
      const response = await prepareResponse({
        project_id: projectId,
        project_supplier_id: project_supplier_id,
        urlParams: JSON.stringify(omit(urlParams, ["obsrv_sid", "obsrv_pid"])),
      });
      const { result, messages } = response.responsePrepare;

      if (!isEmpty(messages)) {
        notify(messages, "error");
      } else if (result) {
        sessionStorage.setItem(
          KEY,
          JSON.stringify({
            id: result.id,
            respondent_key: result.respondent_key,
            status_key: result.status_key,
          })
        );

        const evaluated = await evaluateDevice(result.id, project_supplier_id, projectId);

        if (!evaluated) {
          if (result.should_screen && result.questions) {
            sessionStorage.setItem("redirect_url", result.redirect_url);
            setRespondentId(result.id);
            setQuestions(result.questions);
            setLoading(false);
          } else {
            window.location.href = result.redirect_url;
          }
          return;
        }

        const response = await updateRespondentDevice(result.id, evaluated);

        if (response && response.RespondentDeviceForensiq) {
          const { result: updateResult, messages } = response.RespondentDeviceForensiq;

          if (updateResult === true) {
            if (result.should_screen && result.questions) {
              if (result?.pre_survey_tool?.toLowerCase() === "roundtable") {
                await loadRoundtable(result.id);
              }
              sessionStorage.setItem("redirect_url", result.redirect_url);
              setRespondentId(result.id);
              setQuestions(result.questions);
              setLoading(false);
            } else {
              window.location.href = result.redirect_url;
            }
          } else if (typeof updateResult === "string") {
            window.location.href = updateResult;
          } else {
            console.error("Unexpected update result:", updateResult);
          }

          if (messages && messages.length > 0) {
            notifySimple(["Unexpected error occurred"], "error");
            // capturePageException(new Error(messages.join(", ")), "onPrepareResponse1_failed");
          }
        } else {
          console.error("Failed to update respondent device");
        }
      }
    } catch (err) {
      capturePageException(err, "onPrepareResponse2_failed");
      notifySimple(["Unexpected error occurred"], "error");
    }
  };

  const submitAnswers = (answers: PreSurveyAnswer[]): void => {
    try {
      const roundtableSessionId = fetchRoundtableSessionId();
      submitPreSurvey(project_supplier_id, answers, respondentId, roundtableSessionId)
        .then((response) => {
          const { result, messages } = response.ProjectSupplierPreSurveySubmit;

          if (!isEmpty(messages)) {
            notify(messages, "error");
          } else {
            if (result) {
              if (result.status === "terminated" && result.redirect_url) {
                window.location.href = result.redirect_url;
                return;
              }
              const redirectUrl = sessionStorage.getItem("redirect_url");
              if (redirectUrl && typeof redirectUrl === "string") {
                window.location.href = redirectUrl;
                return;
              }
              notifySimple(["Unexpected error occurred"], "error");
              // capturePageException(new Error(JSON.stringify(result)), "submitAnswers1_failed");
            }
            notifySimple(["You cannot proceed to the survey"], "error");
          }
        })
        .catch(() => {
          notifySimple(["You cannot proceed to the survey"], "error");
        });
    } catch (err) {
      capturePageException(err, "submitAnswers2_failed");
      notifySimple(["Unexpected error occurred"], "error");
    }
  };

  useEffect(() => {
    const key: string = import.meta.env.VITE_APP_DEVICE_FORENSIQ_KEY || null;
    let channel: string = import.meta.env.VITE_APP_DEVICE_FORENSIQ_CHANNEL || null;
    const projectSupplierId =
      typeof urlParams["obsrv_sid"] === "string" ? urlParams["obsrv_sid"] : "";

    if (!key || !channel || !projectSupplierId) {
      void onPrepareResponse();
      return;
    }

    channel = `${channel} - ${projectSupplierId}`;

    const fetchRequestToken = async (respondentId: string, eventId: string) => {
      try {
        const response = await postRequest(`generate-request-token`, {
          RequestIdentifier: respondentId,
          EventIdentifier: eventId,
          UseResponseToken: true,
        });
        const data: RequestTokenResponse = await response.data;
        return data;
      } catch (error) {
        console.error("Error fetching request token:", error);
        //capturePageException(error, "fetchRequestToken_failed");
        return null;
      }
    };

    const script = document.createElement("script");
    script.src = "https://api-cdn.dfiq.net/scripts/forensic-v6.2.5.min.js";
    script.id = "forensic_script_id";
    script.type = "text/javascript";
    script.setAttribute("data-key", key);
    script.setAttribute("data-ur", "");
    script.setAttribute("data-uax", "");
    script.onload = () => {
      invokeApiRef.current = async (
        respondentId: string,
        projectSupplierId: string,
        projectId: string
      ): Promise<JsonObject | null> => {
        const tokenResponse = await fetchRequestToken(respondentId, projectId);

        if (!tokenResponse || !tokenResponse?.token) {
          console.error("Failed to retrieve request token");
          return null;
        }

        return new Promise((resolve, reject) => {
          const uniquenessParam = window.Forensic.createUniquenessParam(projectId),
            geoParam = window.Forensic.createGeoParam(tokenResponse?.countryCode || null);
          const successCallback = (jsonData: JsonObject) => {
            resolve(jsonData);
          };
          const errorCallback = (jsonData: JsonObject) => {
            console.error(jsonData.error.message);
            reject(null);
          };

          const tokenParam = Forensic.createTokenParam(tokenResponse.token, true);
          window.Forensic.forensic(
            respondentId,
            successCallback,
            errorCallback,
            uniquenessParam,
            geoParam,
            tokenParam,
            null,
            tokenResponse?.channel || projectSupplierId
          );
        });
      };
      void onPrepareResponse();
    };
    script.onerror = () => {
      // fail open so flow continues
      void onPrepareResponse();
      console.error("Forensiq script failed to load");
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
      if (roundtableAIScript) {
        document.body.removeChild(roundtableAIScript);
      }
    };
  }, []);

  if (loading) {
    return <Loader />;
  }

  return <PreSurvey questions={questions} submitAnswers={submitAnswers} />;
};

export default ResponsePrepareRoute;
